import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { MsalGuard } from "@azure/msal-angular";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { ScormRuntime12Component } from "./components/scorm-runtime12/scorm-runtime12.component";
import { WelcomeComponent } from "./components/welcome/welcome.component";
import { AppConstants } from "./constants/constants";
import { DirectAccessGuard } from "./guards/direct-access.guard";
import { AuthGuard } from "./guards/auth.guard";
import { ScreenerComponent } from "./views/screener/screener.component";
import { LearnerCourseComponent } from "./components/learner-course/learner-course.component";
import { UpdateUserprofileComponent } from "./components/update-userprofile/update-userprofile.component";
import { LearningGuard } from "./guards/learning.guard";
import { ComponentExitGuard } from "./guards/component-exit.guard";
import { SessionExpiryGuard } from "./guards/session-expiry.guard";
import { ExternalRuntimeComponent } from "./components/external-runtime/external-runtime.component";
import { PublicNavGuard } from "./guards/public-nav.guard";
import { ContactUsComponent } from "./components/footer/contact-us/contact-us.component";
import { AppRoutingPathType } from "./constants/AppRoutingPathType";
import { PrivacyPolicyComponent } from "./components/footer/privacy-policy/privacy-policy.component";
import { CopyrightComponent } from "./components/footer/copyright/copyright.component";
import { AccessibilityComponent } from "./components/footer/accessibility/accessibility.component";
import { InformationFaqComponent } from "./components/header/information-faq/information-faq.component";
import { InformationHowDoesItWorkComponent } from "./components/header/information-how-does-it-work/information-how-does-it-work.component";
import { InformationTutorResourcesComponent } from "./components/header/information-tutor-resources/information-tutor-resources.component";
import { InformationLearnerGuideComponent } from "./components/header/information-learner-guide/information-learner-guide.component";
import { SelfAssessmentGuard } from "./guards/self-assessment.guard";
import { FsPlayerComponent } from "./components/fs-player/fs-player/fs-player.component";
import { MsalsignedinComponent } from "./components/msalsignedin/msalsignedin.component";

const routes: Routes = [
  {
    path: "welcome",
    component: WelcomeComponent,
    canActivate: [PublicNavGuard],
  },
  { path: "msalsignedin", component: MsalsignedinComponent },
  { path: "contact-us", component: ContactUsComponent },
  { path: "privacy-policy", component: PrivacyPolicyComponent },
  { path: "copyright", component: CopyrightComponent },
  { path: "accessibility", component: AccessibilityComponent },
  { path: "screener", component: ScreenerComponent },
  {
    path: "contentplayer/:" + AppConstants.OBJECT_ID,
    component: ScormRuntime12Component,
    canActivate: [DirectAccessGuard],
    canDeactivate: [ComponentExitGuard],
  },
  {
    path: "learner-course",
    component: LearnerCourseComponent,
    canActivate: [MsalGuard, AuthGuard, SessionExpiryGuard],
    canDeactivate: [SelfAssessmentGuard],
    data: {
      appRoutingPathType: AppRoutingPathType.LearnerCourse,
    },
  },
  {
    path: "update-userprofile",
    component: UpdateUserprofileComponent,
    canActivate: [MsalGuard, SessionExpiryGuard],
  },
  {
    path: "learning",
    loadChildren: () =>
      import(`./views/learning/learning.module`).then((m) => m.LearningModule),
    canActivate: [MsalGuard, AuthGuard, SessionExpiryGuard, LearningGuard],
    data: {
      appRoutingPathType: AppRoutingPathType.Learning,
    },
  },
  {
    path: "dashboard",
    component: DashboardComponent,
    canActivate: [MsalGuard, SessionExpiryGuard],
    canDeactivate: [],
  },
  {
    path: "dashboard/admin",
    loadChildren: () =>
      import(`./views/admin/admin.module`).then((m) => m.AdminModule),
    canActivate: [MsalGuard, AuthGuard, SessionExpiryGuard],
    data: {
      appRoutingPathType: AppRoutingPathType.Admin,
    },
  },
  {
    path: "dashboard/tutor",
    loadChildren: () =>
      import(`./views/tutor/tutor.module`).then((m) => m.TutorModule),
    canActivate: [MsalGuard, AuthGuard, SessionExpiryGuard],
    data: {
      appRoutingPathType: AppRoutingPathType.Tutor,
    },
  },
  {
    path: "external-link/:" + AppConstants.OBJECT_ID,
    component: ExternalRuntimeComponent,
  },
  {
    path: "contentplayer",
    redirectTo: "dashboard",
    pathMatch: "full",
  },
  {
    path: "faq",
    component: InformationFaqComponent,
    canActivate: [MsalGuard, SessionExpiryGuard],
  },
  {
    path: "how-does-it-work",
    component: InformationHowDoesItWorkComponent,
    canActivate: [MsalGuard, SessionExpiryGuard],
  },
  {
    path: "tutor-resources",
    component: InformationTutorResourcesComponent,
    canActivate: [MsalGuard, AuthGuard, SessionExpiryGuard],
    data: {
      appRoutingPathType: AppRoutingPathType.TutorResources,
    },
  },
  {
    path: "learner-guide",
    component: InformationLearnerGuideComponent,
    canActivate: [MsalGuard, SessionExpiryGuard],
  },
  {
    path: "reports",
    canActivate: [MsalGuard, SessionExpiryGuard],
    loadChildren: () => 
      import(`./views/reports/reports.module`).then(
        (m) => m.ReportsModule
      )
  },
  {
    path: "fscontentplayer",
    component: FsPlayerComponent
  },
  { path: "", redirectTo: "welcome", pathMatch: "full" },
];

const isIframe = window !== window.parent && !window.opener;
@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: !isIframe ? "enabledBlocking" : "disabled",
      paramsInheritanceStrategy: "always",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
